import React from 'react';
import { Rating } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { FlamingoFund, UnitCategoryCode } from 'funds-model';
import { ReactComponent as StarIcon } from 'common-assets/assets/icons/star.svg';
import { Tooltip } from '../Tooltip/Tooltip';
import { RiskProgressBar } from '../RiskProgressBar/RiskProgressBar';
import { useIsMobile } from '../utils/useIsMobile';
import { Label } from '../Label/Label';
import { LabelWithTooltip } from '../LabelWithTooltip/LabelWithTooltip';
import { zIndex } from '../theme/constants';

import useStyles from './FundDetailsDialog.style';

export interface FundDetailsDialogActionTopBarProps {
  fund: FlamingoFund;
  showAdditionalTab?: boolean;
  unitCategoryCode: UnitCategoryCode;
  processLayout?: boolean;
}

const FundDetailsDialogActionTopBar: React.FC<FundDetailsDialogActionTopBarProps> = ({
  fund,
  showAdditionalTab,
  unitCategoryCode,
  children,
  processLayout = false
}) => {
  const c = useStyles({
    showAdditionalTab,
    titleLength: fund.name.length + (fund.previousName?.length || 0),
    fundTypeColor: fund.fundType.colorHex,
    processLayout
  });
  const [t] = useTranslation('components');
  const isMobile = useIsMobile();

  const { tooltipAppBarZIndex } = zIndex;

  return (
    <div className={c.actionBar}>
      <div className={c.actionBarInfoBox}>
        <div className={c.actionBarInfoCol}>
          <div>
            <Label size="s" color="graphite" className={c.typeFundLabel}>
              {t('fund-list.fundType')}
              <Tooltip
                className={c.tooltip}
                customZIndex={tooltipAppBarZIndex}
                preTitle={fund.fundType.tooltipTitle}
                title={fund.fundType.description}
              />
            </Label>
            <span className={c.typeFund}>{fund.fundType.name}</span>
          </div>
          <div>
            <LabelWithTooltip
              customZIndex={tooltipAppBarZIndex}
              labelText={t('fund-list.horizon')}
              preTitle={isMobile ? t('fund-list.horizon') : ''}
              title={t('fund-details-dialog.horizon-tooltip')}
              size="s"
              color="graphite"
              className={c.horizonLabel}
            />
            <span className={c.horizon}>
              {t(`fund-list.options.horizon.${fund.horizonValue}-years`)}
            </span>
          </div>
        </div>
        <div className={c.actionBarInfoCol}>
          <div className={c.actionBarInfoCell}>
            <RiskProgressBar
              riskValue={fund.risk.value}
              riskLabel={fund.risk.filterLabel}
              withRiskValueLabel
              withRiskInfoLabel
              tooltipZIndex={tooltipAppBarZIndex}
            />
          </div>
          {fund.analizyOnlineRating != null && <div className={c.actionBarInfoCell}>
            <Label size="s" color="graphite" className={c.ratingAolLabel}>
              {t('fund-details-dialog.online-analysis-rating')}
              <Tooltip
                className={c.tooltip}
                customZIndex={tooltipAppBarZIndex}
                preTitle={isMobile ? t('fund-details-dialog.online-analysis-rating') : ''}
                title={t('fund-details-dialog.online-analysis-rating-tooltip')}
              />
            </Label>
            <Rating
              className={c.rating}
              value={fund.analizyOnlineRating}
              readOnly
              icon={<StarIcon className={c.starIcon} />}
              emptyIcon={<StarIcon className={c.emptyStarIcon}/>}
            />
          </div>}
        </div>
      </div>
      <div className={c.actionTopBarContextBox}>
        {children}
      </div>
    </div>
  );
};

export default FundDetailsDialogActionTopBar;
